import type { Includes } from './useOwnerShare'
import './OwnerBar.css'

interface OwnerBarProps {
  includes: Includes
  onToggle: (key: keyof Includes, on: boolean) => void
  onBack: () => void
  onShare: () => void
}

const SECTIONS: { key: keyof Includes; label: string }[] = [
  { key: 'photos', label: 'Photos' },
  { key: 'metrics', label: 'Metrics' },
  { key: 'routine', label: 'Routine' },
]

/**
 * The owner control bar (always visible, top of owner mode). Each section is a
 * toggle that live-shows/hides that part of the preview below; Share opens the
 * Create step. Presentational only — state + actions come from useOwnerShare via
 * OwnerPage. Photos and Metrics toggle independently; the hook blocks turning the
 * last section off.
 */
export function OwnerBar({
  includes,
  onToggle,
  onBack,
  onShare,
}: OwnerBarProps) {
  return (
    <div className='ownerbar'>
      <button
        type='button'
        className='ownerbar__back'
        onClick={onBack}
        aria-label='Back'
      >
        <svg width='18' height='18' viewBox='0 0 16 16' aria-hidden='true'>
          <path
            d='M10 3 5 8l5 5'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.8'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>

      <div
        className='ownerbar__toggles'
        role='group'
        aria-label='What to share'
      >
        {SECTIONS.map(({ key, label }) => {
          const on = includes[key]
          return (
            <button
              key={key}
              type='button'
              className={`ownerbar__toggle${on ? ' ownerbar__toggle--on' : ''}`}
              aria-pressed={on}
              onClick={() => onToggle(key, !on)}
            >
              {label}
            </button>
          )
        })}
      </div>

      <button type='button' className='ownerbar__share' onClick={onShare}>
        Share
      </button>
    </div>
  )
}
